import * as React from 'react'
import { bind } from 'classnames/bind'
import * as styles from '@procore/core-css/src/layouts/_detail_page.scss'

import addSubcomponents from '../utils/addSubcomponents'
import { DivAttributes } from '../utils/types'
import { Visibility } from '../state/Visibility'
import Icon from '../components/Icon'

const cx = bind(styles)

interface Props {
  children?: React.ReactNode
  className?: string
  style?: object
}

interface DetailPageProps extends Props {
  initialIsVisible: boolean
}

const { Provider, Consumer } = React.createContext({
  isVisible: false,
  show: () => {},
  hide: () => {},
  toggle: () => {},
})

const DetailPage = React.forwardRef<
  HTMLDivElement,
  DivAttributes & DetailPageProps
>(({ children, className, initialIsVisible, ...props }, ref) => (
  <Visibility initialIsVisible={initialIsVisible}>
    {visibility => (
      <Provider value={visibility}>
        <div
          ref={ref}
          className={cx(
            'core-l-detail-page',
            {
              'core-l-detail-page--collapsed': !visibility.isVisible,
            },
            className
          )}
          {...props}
        >
          {children}
        </div>
      </Provider>
    )}
  </Visibility>
))

export const Main = React.forwardRef<HTMLDivElement, DivAttributes & Props>(
  ({ children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cx('core-l-detail-page__main', className)}
      {...props}
    >
      {children}
    </div>
  )
)

export const Banner = React.forwardRef<HTMLDivElement, DivAttributes & Props>(
  ({ children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cx('core-l-detail-page__banner', className)}
      {...props}
    >
      {children}
    </div>
  )
)

export const Body = React.forwardRef<HTMLDivElement, DivAttributes & Props>(
  ({ children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cx('core-l-detail-page__body', className)}
      {...props}
    >
      <div className={cx('core-l-detail-page__body__content')}>{children}</div>
    </div>
  )
)

export const Breadcrumbs = React.forwardRef<
  HTMLDivElement,
  DivAttributes & Props
>(({ children, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cx('core-l-detail-page__breadcrumbs', className)}
    {...props}
  >
    {children}
  </div>
))

export const Title = React.forwardRef<HTMLDivElement, DivAttributes & Props>(
  ({ children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cx('core-l-detail-page__title', className)}
      {...props}
    >
      {children}
    </div>
  )
)

export const Subtabs = React.forwardRef<HTMLDivElement, DivAttributes & Props>(
  ({ children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cx('core-l-detail-page__subtabs', className)}
      {...props}
    >
      {children}
    </div>
  )
)

export const Sidebar = React.forwardRef<HTMLDivElement, DivAttributes & Props>(
  ({ children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cx('core-l-detail-page__sidebar', className)}
      {...props}
    >
      {children}
    </div>
  )
)

export const SidebarContent = React.forwardRef<
  HTMLDivElement,
  DivAttributes & Props
>(({ children, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cx('core-l-detail-page__sidebar__content', className)}
    {...props}
  >
    {children}
  </div>
))

export const SidebarFooter = React.forwardRef<
  HTMLDivElement,
  DivAttributes & Props
>(({ children, className, ...props }, ref) => (
  <Consumer>
    {visibility => (
      <div
        ref={ref}
        className={cx('core-l-detail-page__sidebar__footer', className)}
        onClick={visibility.toggle}
        {...props}
      >
        <div className={cx('core-l-detail-page__sidebar__footer__label')}>
          {children}
        </div>
        <div className={cx('core-l-detail-page__sidebar__footer__icon')}>
          <Icon icon="angle-double-left" clickable />
        </div>
      </div>
    )}
  </Consumer>
))

DetailPage.displayName = 'DetailPage'

Banner.displayName = 'DetailPage.Banner'

Body.displayName = 'DetailPage.Body'

Breadcrumbs.displayName = 'DetailPage.Breadcrumbs'

Main.displayName = 'DetailPage.Main'

Sidebar.displayName = 'DetailPage.Sidebar'

SidebarContent.displayName = 'DetailPage.SidebarContent'

SidebarFooter.displayName = 'DetailPage.SidebarFooter'

Subtabs.displayName = 'DetailPage.Subtabs'

Title.displayName = 'DetailPage.Title'

export default addSubcomponents(
  {
    Banner,
    Body,
    Breadcrumbs,
    Consumer,
    Main,
    Provider,
    Sidebar,
    SidebarContent,
    SidebarFooter,
    Subtabs,
    Title,
  },
  DetailPage
)
